import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Chip from '@mui/material/Chip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { SortableContainer, SortableElement } from 'react-sortable-hoc';

const useStyles = makeStyles(theme => ({
    chip: {
        margin: theme.spacing(0.5),
        '&.hidden .MuiChip-label': {
            width: 150,
        },
        '&:not(.hidden), .MuiChip-label': {
            width: 134,
        },
    },
    gridItem: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        outline: 'none',

        '&:focus': {
            textIndent: -2,
        },

        '& .wrapper': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            transform: 'scale(1)',
            span: {
                display: 'none',
            }
        }
    },
    disabled: {
        cursor: 'not-allowed',
    },

    // Helper styles
    stylizedHelper: {
        zIndex: theme.zIndex.modal + 10,
        '&:not(.gridItem), &.gridItem .wrapper': {
            opacity: 0.8,
        },

        '&.gridItem .wrapper': {
            transition: 'transform 150ms ease-in-out',
        }
    },
}));

const Column = SortableElement(({
    data,
    color,
    onAction,
    onDelete,
    className,
    isDisabled,
    isSorted,
    onSort,
    height,
    style: propStyle,
    chipStyle,
    itemIndex,
    isSorting,
}) => {
    const classes = useStyles();

    return (
        <div
            className={clsx(
                classes.wrapper,
                className,
                isDisabled && classes.disabled,
                isSorting && classes.sorting,
            )}
            style={{
                height,
                ...propStyle,
            }}
            tabIndex={-1}
            data-index={itemIndex}>
            <Chip
                key={data.variable}
                clickable={isDisabled || data.indexed}
                size="small"
                color={color}
                label={data.title}
                style={chipStyle}
                className={clsx(classes.chip, classes.hidden)}
                onClick={onAction}
                onDelete={onDelete}
                icon={isSorted === -1
                    ? <KeyboardArrowDownIcon />
                    : isSorted === 1
                        ? <KeyboardArrowUpIcon />
                        : undefined}/>
        </div>
    );
});

const ColumnList = SortableContainer(({
    items,
    onAction,
    onDelete,
    className,
    itemClass,
    isSorting,
    isSorted,
    isDisabled,
    isAvailable,
    getColor,
    onSort,
    type,
    style,
    chipStyle,
}) => {
    return (
        <div className={className}>
            {items.map((c, i) => {
                return (
                    <Column
                        tabbable
                        key={`item-${c.variable}`}
                        color={!isAvailable || isAvailable(c)
                            ? getColor ? getColor(c) : 'default'
                            : 'default'
                        }
                        style={style}
                        chipStyle={chipStyle}
                        disabled={isDisabled}
                        isDisabled={isDisabled}
                        isSorted={isSorted ? isSorted(c) : 0}
                        className={itemClass}
                        index={i}
                        itemIndex={i}
                        data={c}
                        type={type}
                        isSorting={isSorting}
                        onAction={onAction ? () => onAction(c, i) : undefined}
                        onDelete={onDelete ? () => onDelete(c, i) : undefined}
                        onSort={() => onSort(c)}
                    />
                );
            })}
        </div>
    );
});

const ListWrapper = (props) => {
    const classes = useStyles();
    const [items, setItems] = useState(props.items);
    const [isSorting, setIsSorting] = useState(false);
    const [helpers, setHelpers] = useState();

    const onSortStart = (sortEvent, nativeEvent) => {
        setIsSorting(false);
        document.body.style.cursor = 'grabbing';
        setHelpers(current => {
            if (!current) current = [];
            current.push(sortEvent.helper);
            return current;
        });
    };

    const onSortEnd = (sortEvent, nativeEvent) => {
        const { oldIndex, newIndex } = sortEvent;
        setIsSorting(false);
        document.body.style.cursor = '';

        // clean up ghost nodes. SortableContainer is not doing its job
        if (props.helperContainer) {
            (helpers || []).forEach(function (helper) {
                if (props.helperContainer.contains(helper)) {
                    props.helperContainer.removeChild(helper);
                }
            });
            setHelpers();
        }

        props.onSortEnd && props.onSortEnd(oldIndex, newIndex);
    };

    useEffect(() => {
        setItems(props.items);
        setHelpers();
    }, [props.items]);

    return (
        <ColumnList
            {...props}
            hideSortableGhost={false}
            lockToContainerEdges={true}
            helperClass={classes.stylizedHelper}
            distance={1}
            isSorting={isSorting}
            items={items}
            onSortEnd={onSortEnd}
            onSortStart={onSortStart}/>
    );
};

ListWrapper.propTypes = {
    classes: PropTypes.object.isRequired,
    items: PropTypes.array,
    className: PropTypes.string,
    itemClass: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    onSortStart: PropTypes.func,
    onSortEnd: PropTypes.func,
    onAction: PropTypes.func,
    isSorted: PropTypes.func,
    isAvailable: PropTypes.func,
    onSort: PropTypes.func,
    helperContainer: PropTypes.oneOfType([
        PropTypes.func,
        typeof HTMLElement === 'undefined'
            ? PropTypes.any : PropTypes.instanceOf(HTMLElement),
    ]),
};

export {
    ListWrapper as default,
};
